import React, { useState } from "react";
import {
  FiX,
  FiAlertCircle,
  FiClock,
  FiUser,
  FiCreditCard,
  FiTrendingUp,
  FiActivity,
  FiCalendar,
  FiCheckCircle,
} from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

const ViewSellModal = ({ isOpen, onClose, transaction, fetchSellOrders }) => {
  const [isSettling, setIsSettling] = useState(false);
  const [showSettleConfirm, setShowSettleConfirm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [newUtrHash, setNewUtrHash] = useState("");
  const [isUpdatingHash, setIsUpdatingHash] = useState(false);
  if (!isOpen || !transaction) return null;
  console.log("transaction", transaction);

  const handleSettleAmount = async () => {
    try {
      setIsSettling(true);

      const response = await makeRequestAuth("post", "/admin/settleAmount", {
        sellId: transaction._id,
      });

      if (response.error || response.status === false) {
        showErrorToast(response.message || "Failed to settle amount");
        return;
      }

      showSuccessToast(response.message || "Amount settled successfully");

      if (fetchSellOrders) {
        fetchSellOrders(1);
      }

      setShowSettleConfirm(false);
      onClose();
    } catch (error) {
      showErrorToast(error.message || "Failed to settle amount");
    } finally {
      setIsSettling(false);
    }
  };

  const handleUpdateHash = async () => {
    try {
      if (!newUtrHash.trim()) {
        return showErrorToast("Please enter UTR number");
      }

      setIsUpdatingHash(true);

      const response = await makeRequestAuth("post", "/admin/updatePaidHash", {
        sellId: transaction._id,
        paidId: editingPayment._id,
        hash: newUtrHash,
      });

      if (response.error || response.status === false) {
        showErrorToast(response.message || "Failed to update UTR");
        return;
      }

      showSuccessToast(response.message || "UTR updated successfully");

      // Local state update
      transaction.paidList = transaction.paidList.map((item) =>
        item._id === editingPayment._id
          ? {
              ...item,
              hash: newUtrHash,
            }
          : item,
      );

      // Optional refresh
      if (fetchSellOrders) {
        fetchSellOrders(1);
      }

      setEditingPayment(null);
      setNewUtrHash("");
    } catch (error) {
      showErrorToast(error.message || "Failed to update UTR");
    } finally {
      setIsUpdatingHash(false);
    }
  };

  const statusMap = {
    approve: { color: "emerald", label: "Approved" },
    reject: { color: "red", label: "Rejected" },
    pending: { color: "amber", label: "Pending" },
  };

  const currentStatus = statusMap[transaction.status] || {
    color: "gray",
    label: transaction.status,
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const InfoBlock = ({
    label,
    value,
    subValue,
    icon: Icon,
    colorClass = "text-gray-900 dark:text-white",
  }) => (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-white/50 dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50">
      {Icon && <Icon className="mt-1 text-gray-400" size={16} />}
      <div>
        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-0.5">
          {label}
        </p>
        <p className={`text-sm font-bold ${colorClass}`}>{value}</p>
        {subValue && (
          <p className="text-[11px] font-medium text-emerald-500">{subValue}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4 backdrop-blur-md transition-all">
      <div className="bg-white dark:bg-[#0F172A] rounded-3xl shadow-2xl w-full max-w-2xl flex flex-col border border-white/20 overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[95vh]">
        {/* Status Accent Bar */}
        <div className={`h-1.5 w-full bg-${currentStatus.color}-500`} />

        {/* Header */}
        <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-xl font-black text-gray-800 dark:text-white flex items-center gap-2">
              Transaction Details
              <span
                className={`text-[10px] px-2 py-0.5 rounded-md bg-${currentStatus.color}-100 dark:bg-${currentStatus.color}-500/10 text-${currentStatus.color}-600 dark:text-${currentStatus.color}-400 border border-${currentStatus.color}-200 dark:border-${currentStatus.color}-500/20`}
              >
                {currentStatus.label.toUpperCase()}
              </span>
            </h2>
            <p className="text-xs text-gray-400 font-mono mt-1">
              Order ID: {transaction.orderId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="bg-gray-100 dark:bg-gray-800 p-2 rounded-full text-gray-500 hover:text-red-500 transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-8 overflow-y-auto custom-scrollbar space-y-8">
          {/* Main Financial Grid */}
          <section>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
              <InfoBlock
                label="Asset & Wallet"
                value={`${transaction.walletType} ${
                  transaction.assetSymbol ? `(${transaction.assetSymbol})` : ""
                }`}
                icon={FiActivity}
              />

              <InfoBlock
                label="Amount & Value"
                value={`${transaction.amount} Units`}
                subValue={`Total: ₹${transaction.totalAmount}`}
                icon={FiTrendingUp}
              />

              <InfoBlock
                label="Exchange Rate"
                value={`₹${transaction.rate} / unit`}
                icon={FiTrendingUp}
              />

              <InfoBlock
                label="Amount To Pay"
                value={`₹${(transaction.amount * transaction.rate).toFixed(2)}`}
                icon={FiCreditCard}
              />

              <InfoBlock
                label="Paid Amount"
                value={`₹${transaction.paidAmount}`}
                // subValue={`Remaining: ₹${transaction.paidAmount}`}
                icon={FiClock}
              />

              {/* <InfoBlock
                label="Remaining Amount"
                value={`₹${transaction.remainAmount}`}
                // subValue={`Remaining: ₹${transaction.remainAmount}`}
                icon={FiAlertCircle}
              /> */}

              <div className="flex flex-col gap-2">
                <InfoBlock
                  label="Remaining Amount"
                  value={`₹${transaction.remainAmount}`}
                  icon={FiAlertCircle}
                />
                <button
                  onClick={() => setShowSettleConfirm(true)}
                  disabled={transaction.remainAmount <= 0 || isSettling}
                  className={`w-full py-2.5 px-4 text-xs font-bold text-white transition-all rounded-xl shadow-sm flex items-center justify-center gap-1.5
    ${
      transaction.remainAmount <= 0
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] dark:bg-emerald-500 dark:hover:bg-emerald-600"
    }
  `}
                >
                  <FiCheckCircle size={14} />
                  {isSettling ? "Processing..." : "Settle Amount"}
                </button>
              </div>
            </div>
          </section>

          {/* User Information */}
          <section className="bg-blue-50/50 dark:bg-blue-500/5 p-5 rounded-2xl border border-blue-100 dark:border-blue-500/10">
            <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <FiUser size={14} /> Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-[10px] text-gray-400 uppercase mb-1">
                  Full Name
                </p>
                <p className="text-sm font-bold text-gray-700 dark:text-gray-200">
                  {transaction.userId?.name || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase mb-1">
                  Email Address
                </p>
                <p className="text-sm font-bold text-gray-700 dark:text-gray-200 break-all">
                  {transaction.userId?.email || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase mb-1">
                  Contact
                </p>
                <p className="text-sm font-bold text-gray-700 dark:text-gray-200">
                  {transaction.userId?.mobile || "N/A"}
                </p>
              </div>
            </div>
          </section>

          {/* Payment Method Details */}
          {transaction.paymentMethods && (
            <section>
              <div className="flex justify-between items-end mb-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <FiCreditCard size={14} /> Payout Method
                </h3>
                {transaction.paymentMethods.isPrimary && (
                  <span className="text-[9px] font-black bg-purple-100 dark:bg-purple-500/20 text-purple-600 px-2 py-0.5 rounded">
                    PRIMARY
                  </span>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="mb-4">
                  <span className="text-lg font-black text-gray-900 dark:text-white">
                    {transaction.paymentMethods.methodType}
                  </span>
                  <p className="text-ms text-gray-400">
                    Name: {transaction.paymentMethods.label}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                  {Object.entries(transaction.paymentMethods.details || {}).map(
                    ([key, value]) => (
                      <div key={key}>
                        <p className="text-[9px] text-gray-400 uppercase font-bold mb-1">
                          {key.replace(/([A-Z])/g, " $1")}
                        </p>
                        <p className="text-sm font-mono text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 p-2 rounded-lg border border-gray-100 dark:border-gray-800">
                          {String(value)}
                        </p>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Payment Timeline/History */}
          {/* {transaction.paidList?.length > 0 && (
            <section>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <FiClock size={14} /> Payment Timeline
              </h3>
              <div className="relative border-l-2 border-gray-100 dark:border-gray-800 ml-2 space-y-4 pb-2">
                {[...transaction.paidList]
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((item, idx) => (
                    <div key={idx} className="relative pl-6">
                      <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 border-4 border-white dark:border-[#0F172A]" />
                      <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/40 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                        <div>
                          <p className="text-sm font-black text-gray-900 dark:text-white">
                            ₹{item.amount}
                          </p>
                          <p className="text-sm font-black text-gray-900 dark:text-white">
                            UTR: {item.hash}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {formatDate(Number(item.createdAt))}
                          </p>
                        </div>
                        <span className="text-[10px] font-bold text-emerald-500 uppercase">
                          Confirmed
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          )} */}

          {/* Payment Timeline/History */}
          {transaction.paidList?.length > 0 && (
            <section>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <FiClock size={14} /> Payment Timeline
              </h3>
              <div className="relative border-l-2 border-gray-100 dark:border-gray-800 ml-2 space-y-4 pb-2">
                {[...transaction.paidList]
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((item, idx) => (
                    <div key={idx} className="relative pl-6">
                      <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 border-4 border-white dark:border-[#0F172A]" />
                      <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/40 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                        <div>
                          <p className="text-sm font-black text-gray-900 dark:text-white">
                            ₹{item.amount}
                          </p>
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mt-0.5">
                            UTR:{" "}
                            <span className="font-mono text-gray-900 dark:text-gray-200 font-bold">
                              {item.hash}
                            </span>
                          </p>
                          <p className="text-[10px] text-gray-400 mt-1">
                            {formatDate(Number(item.createdAt))}
                          </p>
                        </div>

                        {/* Status and Edit Button Container */}
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[10px] font-bold text-emerald-500 uppercase bg-emerald-50 dark:bg-emerald-500/10 px-2 py-1 rounded-md">
                            Confirmed
                          </span>

                          <button
                            onClick={() => {
                              setEditingPayment(item);
                              setNewUtrHash(item.hash);
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors shadow-sm active:scale-95"
                            title="Edit Payment"
                          >
                            <svg
                              stroke="currentColor"
                              fill="none"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-3.5 h-3.5"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          )}

          {/* Edit UTR Modal */}
          {editingPayment && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm animate-fade-in">
              <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-2xl space-y-6 transform scale-100 transition-all">
                {/* Header */}
                <div className="flex justify-between items-center pb-2 border-b border-gray-100 dark:border-gray-800">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      Edit UTR Number
                    </h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Updating details for transaction of ₹
                      {editingPayment.amount}
                    </p>
                  </div>
                  <button
                    onClick={() => setEditingPayment(null)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <svg
                      stroke="currentColor"
                      fill="none"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                {/* Form Content */}
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase font-bold tracking-wider block mb-1.5">
                      UTR / Transaction Hash
                    </label>
                    <input
                      type="text"
                      value={newUtrHash}
                      onChange={(e) => setNewUtrHash(e.target.value)}
                      placeholder="Enter new UTR number"
                      className="w-full px-3 py-2.5 font-mono text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl focus:outline-none focus:border-blue-500 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all placeholder:text-gray-400 font-bold"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end pt-2">
                  <button
                    onClick={() => setEditingPayment(null)}
                    className="px-4 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl border border-transparent hover:border-gray-200 dark:hover:border-gray-700 transition-all active:scale-95"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateHash}
                    disabled={isUpdatingHash}
                    className="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-xl shadow-md shadow-blue-500/10 transition-all active:scale-[0.98] flex items-center gap-1.5"
                  >
                    <svg
                      stroke="currentColor"
                      fill="none"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-3.5 h-3.5"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {isUpdatingHash ? "Updating..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rejection Alert */}
          {transaction.status === "reject" && (
            <div className="flex gap-4 bg-red-50 dark:bg-red-500/5 p-5 rounded-2xl border border-red-100 dark:border-red-500/20">
              <FiAlertCircle className="text-red-500 shrink-0" size={20} />
              <div>
                <p className="text-xs font-bold text-red-600 uppercase tracking-tight">
                  Rejection Reason
                </p>
                <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                  {transaction.rejectReason || "No specific reason provided."}
                </p>
              </div>
            </div>
          )}

          {/* Settle Confirmation Modal */}
          {showSettleConfirm && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 p-6">
                <div className="flex items-start gap-3">
                  <div className="p-3 rounded-full bg-emerald-100 dark:bg-emerald-500/10">
                    <FiCheckCircle
                      size={22}
                      className="text-emerald-600 dark:text-emerald-400"
                    />
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Confirm Settlement
                    </h3>

                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                      Are you sure you want to settle the remaining amount of{" "}
                      <span className="font-bold text-emerald-600">
                        ₹{transaction.remainAmount}
                      </span>
                      ?
                    </p>

                    <p className="text-xs text-gray-400 mt-2">
                      This action will complete the transaction.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowSettleConfirm(false)}
                    disabled={isSettling}
                    className="px-4 py-2 text-sm font-semibold rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleSettleAmount}
                    disabled={isSettling}
                    className="px-5 py-2 text-sm font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isSettling ? "Processing..." : "Yes, Settle"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer Timestamps */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2 text-gray-400">
              <FiCalendar size={12} />
              <span className="text-[10px]">
                Created: {formatDate(transaction.createdAt)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-400 justify-end">
              {/* <FiClock size={12} /> */}
              <span className="text-[10px]">
                {/* Updated: {formatDate(transaction.updatedAt)} */}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Action */}
        <div className="px-8 py-5 bg-gray-50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-8 py-2.5 rounded-2xl bg-white dark:bg-gray-700 text-gray-600 dark:text-white font-bold text-sm shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-600 transition-all active:scale-95"
          >
            Close Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewSellModal;
